import { useState, useEffect } from 'react'
import Formsy from 'formsy-react'
import { branch, renderComponent } from 'recompose'

import { withRouter, withTranslate } from '@i18n/utils'
import { useMediaQuery } from 'react-responsive'

import { GRID_BREAKPOINT_SM } from 'shared/breakpoints'
import useBookingValidation from 'shared/hooks/useBookingValidation'
import featureToggles from 'shared/featureToggles'
import { useSendGTAGEvent } from 'analytics/utils'
import { DOCUMENT_TYPE_NUMBER_PATTERN } from 'shared/components/DocumentDataFields/constants'
import { CUSTOMER_TYPE_INDIVIDUAL } from 'shared/constants'

import LoginSignUpContainer from '@components/LoginSignUpContainer'
import ErrorMessage from '@components/ErrorMessage'

import PriceBreakdownModal from '@components/PriceBreakdownModal'

import PaymentMethodSection from 'purchase-flow/brazil/components/PaymentMethodSection'

import { makePurchasePendingSelector } from 'basket/selectors'
import { userAccountIdSelector } from 'auth/selectors'
import {
    lastBookingBasketItemSelector,
    ratesPendingSelector,
    locationDataSelector,
    guestUserCardVerifiedSelector,
} from '@booking/selectors'
import { getPaymentMethod } from 'booking/utils'
import { verifyGuestCreditCardResetState } from 'booking/actions'

import withRates from '../../withRates'
import withPurchase from '../../withPurchase'
import FooterSection from './FooterSection'
import ProductDetails from './ProductDetails'
import PromoCodeSection from './PromoCodeSection'
import FlightInfo from './FlightInfo'
import PersonalDetails from 'booking/components/GuestCheckoutComponents/PersonalDetails'

import bookingStyles from './styles.module.scss'
import { cancellationSelector, reservationViaUrlSelector } from 'dashboard-transactions/selectors'
import { withRouteParams } from 'shared/hoc'

const personalDataFormInitialState = {
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    documentType: CUSTOMER_TYPE_INDIVIDUAL,
}

const ReviewPay = ({
    bookingData,
    user,
    rate,
    selectedCreditCard,
    makePurchasePending,
    ratesPending,
    allowPurchase,
    lastBookingBasketItem,
    onPayForParkingClick,
    setCreditCard,
    hasPromoCodeComponent,
    selectVehicle,
    selectedVehicle,
    isEventFlow,
    currentItemIndex,
    setOutboundNumber,
    setInboundNumber,
    locationData,
    showPixPaymentMethod,
    userId,
    guestReservationAmendPolicy,
    guestUserCardIsVerified,
    existingReservation,
    verifyGuestCreditCardResetState,
}) => {
    const [priceBreakdownModalOpen, setPriceBreakdownModalOpen] = useState(false)
    const { error, isLoading } = useBookingValidation()
    const sendGTAGEvent = useSendGTAGEvent()

    const [outboundNumberError, setOutboundNumberError] = useState(false)
    const [inboundNumberError, setInboundNumberError] = useState(false)

    // guestReservationAmendPolicy has 2 default fields which we don't want to remove
    const guestUserPersonalData = Object.keys(guestReservationAmendPolicy)?.length > 2 && {
        firstName: guestReservationAmendPolicy.parkerFirstName,
        lastName: guestReservationAmendPolicy.parkerLastName,
        email: guestReservationAmendPolicy.parkerEmailAddress,
        phone: guestReservationAmendPolicy.parkerMobile,
    }
    const [personalDataForm, setPersonalDataForm] = React.useState(
        guestUserPersonalData || personalDataFormInitialState
    )

    // Only guest user can end up here without user object e.g. account
    const isGuest = featureToggles.hasGuestCheckoutFlow && !user

    const paymentMethods = user ? user.creditCards : guestUserCardIsVerified ? [guestUserCardIsVerified] : null

    const purchaseError = makePurchasePending instanceof Error ? makePurchasePending : ''

    const discountedTotalPrice =
        lastBookingBasketItem?.promoCode &&
        lastBookingBasketItem?.promoCode.statusCode !== -1 &&
        lastBookingBasketItem?.promoCode.discountTotal

    const showPromoCodeSection = !existingReservation?.reservationId
    const promoCodeBtnDisabled =
        !rate || (ratesPending && !(ratesPending instanceof Error)) || makePurchasePending === true

    const isDesktop = useMediaQuery({ minWidth: GRID_BREAKPOINT_SM })

    const showFlightInfo =
        featureToggles.showFlightInfo && (locationData?.isOutboundFlight || locationData?.isInboundFlight)

    const flightInfoIsNotFilled =
        featureToggles.showFlightInfo &&
        ((locationData?.isOutboundFlight && !bookingData.outboundNumber) ||
            (locationData?.isInboundFlight && !bookingData.inboundNumber) ||
            outboundNumberError ||
            inboundNumberError)

    const validateDocumentNumber = () => {
        const pattern = DOCUMENT_TYPE_NUMBER_PATTERN[personalDataForm.documentType] || /^.+$/
        return pattern.test(personalDataForm.documentNumber)
    }

    const guestCheckoutDataIsNotFilled =
        isGuest &&
        (Object.values(personalDataForm).some(field => !field || field.trim() == '') ||
            (featureToggles.brNeoPurchaseFlow && !validateDocumentNumber()))

    const submitBtnDisabled = isLoading || !selectedCreditCard || flightInfoIsNotFilled || guestCheckoutDataIsNotFilled

    useEffect(() => {
        if (selectedVehicle.length === 0 && user) {
            selectVehicle({ ...bookingData.vehicles[0] })
        }
    }, [user])

    useEffect(() => {
        if (bookingData.outboundNumber?.length === 0) {
            setOutboundNumberError(true)
        } else setOutboundNumberError(false)
    }, [bookingData.outboundNumber])

    useEffect(() => {
        if (bookingData.inboundNumber?.length === 0) {
            setInboundNumberError(true)
        } else setInboundNumberError(false)
    }, [bookingData.inboundNumber])

    const additionalServicePrice = () =>
        bookingData?.additionalServices?.items.reduce((acc, item) => acc + item.value, 0)

    const totalPrice = () => {
        if (additionalServicePrice()) {
            return (
                additionalServicePrice() +
                (discountedTotalPrice != null ? discountedTotalPrice : rate?.amount || bookingData?.event?.amount)
            )
        }
        return discountedTotalPrice != null ? discountedTotalPrice : rate?.amount || bookingData?.product?.amount
    }

    const editedReservationNewTotalPrice = () => {
        const newRatePrice = rate.amount - existingReservation?.amount + guestReservationAmendPolicy?.amendmentFee

        if (newRatePrice !== null) {
            if (newRatePrice > 0) {
                return {
                    amount: newRatePrice,
                    label: <T>booking.total.difference.to.pay.label</T>,
                }
            }
            if (newRatePrice < 0) {
                return {
                    amount: Math.abs(newRatePrice),
                    label: <T>booking.refund.amount.label</T>,
                }
            }
            return { amount: newRatePrice, label: <T>booking.final.price.label</T> }
        }
    }

    const showPriceDifference = Number.isFinite(editedReservationNewTotalPrice()?.amount)

    const onSubmitHandler = () => {
        sendGTAGEvent('payment_method_selected', {
            payment_type: getPaymentMethod(selectedCreditCard?.id),
        }),
            onPayForParkingClick(
                bookingData?.inboundNumber,
                bookingData?.outboundNumber,
                bookingData?.additionalServices?.value,
                { userPersonalData: personalDataForm, vehicle: bookingData.vehicles[0] },
                existingReservation?.reservationId
            )
    }

    const onChangePersonalDataForm = event => {
        const { name, value } = event.target
        setPersonalDataForm(formData => ({ ...formData, [name]: value }))
    }

    const removeGuestUserCard = () => {
        verifyGuestCreditCardResetState()
        setCreditCard(null)
    }

    return (
        <div className={isDesktop && 'card-section'}>
            <>
                <div className={!isDesktop && 'card-section'}>
                    <ProductDetails
                        rate={rate}
                        discountedTotalPrice={discountedTotalPrice}
                        startDate={isEventFlow ? bookingData.date.startDate : rate.fromDate}
                        endDate={isEventFlow ? bookingData.date.endDate : rate.toDate}
                        locationName={isEventFlow ? bookingData.event?.eventName : rate?.locationName}
                        vehicles={bookingData.vehicles}
                        additionalServices={bookingData?.additionalServices?.items}
                    />

                    {showPromoCodeSection && (
                        <PromoCodeSection
                            promoCodeBtnDisabled={promoCodeBtnDisabled}
                            user={user}
                            rate={rate}
                            currentItemIndex={currentItemIndex}
                        />
                    )}
                </div>
                {priceBreakdownModalOpen && (
                    <PriceBreakdownModal
                        isLoading={makePurchasePending === true}
                        disabled={!allowPurchase}
                        onClose={() => setPriceBreakdownModalOpen(false)}
                        onClick={() => setPriceBreakdownModalOpen(false)}
                        onClickButtonLabel={<T>booking.review.pay.breakdown.modal.confirm.button</T>}
                        error={purchaseError}
                        additionalServices={bookingData?.additionalServices?.items}
                        additionalServicePrice={additionalServicePrice}
                        editedReservationNewTotalPrice={editedReservationNewTotalPrice()}
                        newPrice={rate.amount}
                        oldPrice={existingReservation?.amount}
                        amendmentFee={guestReservationAmendPolicy?.amendmentFee}
                        showPriceDifference={showPriceDifference}
                    />
                )}
                {!user && <PersonalDetails formData={personalDataForm} onChangeFormData={onChangePersonalDataForm} />}
                {showFlightInfo && (
                    <FlightInfo
                        setOutboundNumber={setOutboundNumber}
                        setInboundNumber={setInboundNumber}
                        bookingData={bookingData}
                        outboundNumberError={outboundNumberError}
                        setOutboundNumberError={setOutboundNumberError}
                        inboundNumberError={inboundNumberError}
                        setInboundNumberError={setInboundNumberError}
                        locationData={locationData}
                    />
                )}
                <div className={!isDesktop && 'card-section'}>
                    <div className={bookingStyles.paymentMethods}>
                        <PaymentMethodSection
                            selectedPaymentMethod={selectedCreditCard}
                            setPaymentMethod={setCreditCard}
                            paymentMethods={paymentMethods}
                            showPixPaymentMethod={showPixPaymentMethod}
                            userId={userId}
                            guestUserFlow={!user}
                            removeGuestUserCard={removeGuestUserCard}
                        />
                    </div>
                    <Formsy onSubmit={onSubmitHandler}>
                        <FooterSection
                            rate={rate || bookingData?.event}
                            disabled={submitBtnDisabled}
                            loading={isLoading}
                            setPriceBreakdownModalOpen={setPriceBreakdownModalOpen}
                            total={totalPrice()}
                            rateCurrencySymbol={rate?.currencySymbol || bookingData?.event?.currencySymbol}
                            editedReservationNewTotalPrice={editedReservationNewTotalPrice()}
                            newPrice={rate.amount}
                            oldPrice={existingReservation?.amount}
                            amendmentFee={guestReservationAmendPolicy?.amendmentFee}
                            showPriceDifference={showPriceDifference}
                        />
                    </Formsy>
                </div>
            </>
            {error && <ErrorMessage className="mt-5" messages={[error]} />}
        </div>
    )
}

export default compose(
    withRouter,
    withRouteParams,
    withTranslate,
    withPurchase,
    connect(
        createStructuredSelector({
            makePurchasePending: makePurchasePendingSelector,
            lastBookingBasketItem: lastBookingBasketItemSelector,
            ratesPending: ratesPendingSelector,
            locationData: locationDataSelector,
            userId: userAccountIdSelector,
            guestReservationAmendPolicy: cancellationSelector,
            existingReservation: reservationViaUrlSelector,
            guestUserCardIsVerified: guestUserCardVerifiedSelector,
        }),
        { verifyGuestCreditCardResetState }
    ),
    branch(
        ({ user }) => !user && !featureToggles.hasGuestCheckoutFlow,
        renderComponent(LoginSignUpContainer),
        renderComponent(ReviewPay)
    )
)(withRates(ReviewPay))
